import { h, Fragment } from 'preact'
import { useState } from 'preact/hooks'

import Form from 'components/Form'
import Switch from 'components/Switch'
import OrganizationEditForm from 'components/OrganizationEditForm'

export default function MarketplaceSettingsPage({
  organization, changes, setChanges
}) {
  return <OrganizationEditForm {...{
    className: 'OrganizationAdminPage-padded',
    organization,
    value: changes,
    onChange: setChanges,
    render: props => {
      return <Fragment>
        <MarketplaceEnableSection {...{...props, organization}}/>
        <MarketplaceSettingsSection {...{...props, organization}}/>
        <MarketplaceCategoriesSection {...{...props, organization}}/>
        {props.formControls()}
      </Fragment>
    }
  }}/>
}

function MarketplaceEnableSection({ form }) {
  return <Form.Row>
    <Form.Item>
      <Form.Label>ENABLE MARKETPLACE</Form.Label>
      <p>Enable marketplace for your organization.</p>
      {
        form.bindInput({
          valueProp: 'marketplace_enabled',
          validateOnChange: true,
          input: <Switch />
        })
      }
    </Form.Item>
  </Form.Row>
}

function MarketplaceSettingsSection({ form, organization }) {
  const marketplaceEnabled = form.getValue('marketplace_enabled') ?? organization.marketplace_enabled

  // Helper function to get current marketplace settings
  const getCurrentMarketplaceSettings = () => {
    const currentSettings = form.getValue('marketplace_settings') || organization.marketplace_settings || {}
    return {
      auto_approve_sellers: false,
      require_shipping_address: true,
      allow_digital_products: true,
      featured_products_limit: 10,
      categories: [],
      ...currentSettings
    }
  }

  // Helper function to update marketplace settings
  const updateMarketplaceSetting = (key, settingValue) => {
    const currentSettings = getCurrentMarketplaceSettings()
    const newSettings = { ...currentSettings, [key]: settingValue }

    // Update the form value through the onChange mechanism
    const currentFormValue = form.getValue() || {}
    const updatedFormValue = {
      ...currentFormValue,
      marketplace_settings: newSettings
    }

    // Trigger the form's onChange handler
    if (form.onChange) {
      form.onChange(updatedFormValue)
    }
  }

  const currentSettings = getCurrentMarketplaceSettings()

  return <Fragment>
    <Form.Row>
      <Form.Item>
        <Form.Label>AUTO-APPROVE SELLERS</Form.Label>
        <p>Automatically approve new sellers without manual review.</p>
        <Switch
          checked={currentSettings.auto_approve_sellers}
          onChange={(checked) => updateMarketplaceSetting('auto_approve_sellers', checked)}
          disabled={!marketplaceEnabled}
          style={marketplaceEnabled ? {} : { opacity: 0.5 }}
        />
      </Form.Item>
    </Form.Row>

    <Form.Row>
      <Form.Item>
        <Form.Label>REQUIRE SHIPPING ADDRESS</Form.Label>
        <p>Require customers to provide shipping address for orders.</p>
        <Switch
          checked={currentSettings.require_shipping_address}
          onChange={(checked) => updateMarketplaceSetting('require_shipping_address', checked)}
          disabled={!marketplaceEnabled}
          style={marketplaceEnabled ? {} : { opacity: 0.5 }}
        />
      </Form.Item>
    </Form.Row>

    <Form.Row>
      <Form.Item>
        <Form.Label>ALLOW DIGITAL PRODUCTS</Form.Label>
        <p>Allow sellers to list digital products and services.</p>
        <Switch
          checked={currentSettings.allow_digital_products}
          onChange={(checked) => updateMarketplaceSetting('allow_digital_products', checked)}
          disabled={!marketplaceEnabled}
          style={marketplaceEnabled ? {} : { opacity: 0.5 }}
        />
      </Form.Item>
    </Form.Row>

    <Form.Row>
      <Form.Item>
        <Form.Label>FEATURED PRODUCTS LIMIT</Form.Label>
        <input
          type="number"
          min="1"
          max="50"
          value={currentSettings.featured_products_limit}
          onChange={(e) => updateMarketplaceSetting('featured_products_limit', parseInt(e.target.value, 10) || 10)}
          disabled={!marketplaceEnabled}
          placeholder="10"
          style={{
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '100%',
            opacity: marketplaceEnabled ? 1 : 0.5
          }}
        />
      </Form.Item>
    </Form.Row>
  </Fragment>
}

function MarketplaceCategoriesSection({ form, organization }) {
  const [newCategory, setNewCategory] = useState('')
  const marketplaceEnabled = form.getValue('marketplace_enabled') ?? organization.marketplace_enabled

  // Helper function to get current marketplace settings
  const getCurrentMarketplaceSettings = () => {
    const currentSettings = form.getValue('marketplace_settings') || organization.marketplace_settings || {}
    return {
      auto_approve_sellers: false,
      require_shipping_address: true,
      allow_digital_products: true,
      featured_products_limit: 10,
      categories: [],
      ...currentSettings
    }
  }

  // Helper function to update marketplace settings
  const updateMarketplaceSettings = (newSettings) => {
    const currentFormValue = form.getValue() || {}
    const updatedFormValue = {
      ...currentFormValue,
      marketplace_settings: newSettings
    }

    if (form.onChange) {
      form.onChange(updatedFormValue)
    }
  }

  const currentSettings = getCurrentMarketplaceSettings()
  const categories = currentSettings.categories

  const addCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()]
      const newSettings = { ...currentSettings, categories: updatedCategories }
      updateMarketplaceSettings(newSettings)
      setNewCategory('')
    }
  }

  const removeCategory = (categoryToRemove) => {
    const updatedCategories = categories.filter(cat => cat !== categoryToRemove)
    const newSettings = { ...currentSettings, categories: updatedCategories }
    updateMarketplaceSettings(newSettings)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addCategory()
    }
  }

  return <Fragment>
    <Form.Row>
      <Form.Item>
        <Form.Label>MARKETPLACE CATEGORIES</Form.Label>
        <p>Define categories for organizing marketplace products.</p>

        {/* Existing Categories */}
        {categories.length > 0 && (
          <div className="marketplace-categories-list" style={{ marginBottom: '1rem' }}>
            {categories.map((category, index) => (
              <div key={index} className="category-item" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0.5rem',
                padding: '0.5rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
              }}>
                <span style={{ flex: 1 }}>{category}</span>
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  disabled={!marketplaceEnabled}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: marketplaceEnabled ? 'pointer' : 'not-allowed',
                    opacity: marketplaceEnabled ? 1 : 0.5
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add New Category */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter category name"
            disabled={!marketplaceEnabled}
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              opacity: marketplaceEnabled ? 1 : 0.5
            }}
          />
          <button
            type="button"
            onClick={addCategory}
            disabled={!marketplaceEnabled || !newCategory.trim()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (marketplaceEnabled && newCategory.trim()) ? 'pointer' : 'not-allowed',
              opacity: (marketplaceEnabled && newCategory.trim()) ? 1 : 0.5
            }}
          >
            Add Category
          </button>
        </div>
      </Form.Item>
    </Form.Row>
  </Fragment>
}
